import React, { useEffect, useState } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Box, Typography, Switch, FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText } from '@material-ui/core';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SnackbarOmni from 'allGlobalComponents/SnackbarOmni';
import TableOmniNewAnimated from 'allGlobalComponents/TableOmniNewAnimated';
import ButtonOmniNew from 'allGlobalComponents/ButtonOmniNew';
import ModalOmni from 'allGlobalComponents/ModalOmni';
import FormFieldOmni from 'allGlobalComponents/FormFieldOmni';
import SearchableDropdown from 'allGlobalComponents/SearchableDropdown';
import DateFilterWithExport from 'allGlobalComponents/DateFilterWithExport';
import { useSnackbar, usePagination, useApi } from 'app/hooks';
import DeleteIcon from '@mui/icons-material/Delete';
import ButtonOmni from 'allGlobalComponents/ButtonOmni';

const useStyles = makeStyles(theme => ({
    formContent: {
        padding: theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5)
    },
    buttonContainer: {
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing(2)
    },
    switch: {
        '& .MuiSwitch-switchBase': {
            color: theme.palette.type === 'dark' ? '#bdbdbd' : undefined
        },
        '& .MuiSwitch-track': {
            backgroundColor: theme.palette.type === 'dark' ? '#757575' : undefined
        }
    },
    toggleContent: {
        padding: theme.spacing(3),
        textAlign: 'left'
    }
}));

const INITIAL_FORM = {
    clientId: '',
    channelEntries: [{ channelId: '', channelAppIds: [] }]
};

function ClientChannelsTab() {
    const classes = useStyles();
    const theme = useTheme();

    // Channels that don't require a channel app selection.
    // For these, we skip the channel app dropdown entirely and omit metaAppIds from the payload.
    const NO_APP_CHANNELS = ['sms', 'website', 'email', 'web'];

    // Checks whether the given channelId belongs to a "no app" channel (sms/website/email).
    // We match against both `code` and `name` (lowercased) to be safe across backend variations.
    const isNoAppChannel = (channelId) => {
        const ch = channels.find(c => c.id.toString() === channelId?.toString());
        if (!ch) return false;
        const code = (ch.code || '').toLowerCase();
        const name = (ch.name || '').toLowerCase();
        return NO_APP_CHANNELS.includes(code) || NO_APP_CHANNELS.includes(name);
    };

    // Hooks
    const { snackbar, showSuccess, showError, showWarning } = useSnackbar();
    const { currentParams, setCurrentParams, totalItems, setTotalItems, resetPage } = usePagination(50);
    const { isLoading, request } = useApi();

    // State
    const [data, setData] = useState([]);
    const [clients, setClients] = useState([]);
    const [channels, setChannels] = useState([]);
    const [createOpen, setCreateOpen] = useState(false);
    const [formData, setFormData] = useState(INITIAL_FORM);
    const [saving, setSaving] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [togglingId, setTogglingId] = useState(null);
    const [toggleOpen, setToggleOpen] = useState(false);
    const [channelApps, setChannelApps] = useState([]);
    const [loadingChannelApps, setLoadingChannelApps] = useState(false);

    // Filter state
    const [filterValues, setFilterValues] = useState({
        clientId: ''
    });
    const [refreshKey, setRefreshKey] = useState(0);

    // Fetch clients for dropdown
    const fetchClients = () => {
        request({
            module: 'Business',
            method: 'get_pagination',
            payload: {
                page: 0,
                limit: 1000,
                columns: '*',
                sortby: 'ASC',
                orderby: 'id',
                where: 'id != $1 AND displayed = false order by id',
                values: 0
            },
            onSuccess: (response) => {
                const clientsList = response.data.data.clients.clients || [];
                setClients(clientsList);
            },
            onError: (message) => showError(message || 'Error fetching clients')
        });
    };

    // Fetch channels for dropdown
    const fetchChannels = () => {
        request({
            module: 'saChannels',
            method: 'getChannels',
            payload: {},
            onSuccess: (response) => {
                const channelsList = response.data.data.channels || [];
                setChannels(channelsList);
            },
            onError: (message) => showError(message || 'Error fetching channels')
        });
    };

    // Fetch all client channels (no filter)
    const fetchAllClientChannels = () => {
        request({
            module: 'saChannels',
            method: 'clientChannelsAll',
            payload: {},
            onSuccess: (response) => {
                const list = response.data.data.data || response.data.data || [];
                const items = Array.isArray(list) ? list : [];
                setData(items);
                setTotalItems(items.length);
            },
            onError: (message) => {
                showError(message || 'Error fetching client channels');
                setData([]);
                setTotalItems(0);
            }
        });
    };

    // Fetch client channels by clientId (filter)
    const fetchClientChannels = (clientId) => {
        if (!clientId) return;

        request({
            module: 'saChannels',
            method: 'clientChannelsList',
            payload: { clientId },
            onSuccess: (response) => {
                const list = response.data.data.data || response.data.data || [];
                const items = Array.isArray(list) ? list : [];
                setData(items);
            },
            onError: (message) => {
                showError(message || 'Error fetching client channels');
                setData([]);
                setTotalItems(0);
            }
        });
    };

    // Fetch Channel Apps For Create / Edit
    // `preselectIds` (optional) — ids to keep selected after apps load (used when opening edit)
    const fetchChannelAppsForChannel = (channelId, preselectIds = null) => {
        if (!channelId) {
            setChannelApps([]);
            return;
        }
        const selectedChannel = channels.find(ch => ch.id.toString() === channelId.toString());
        if (!selectedChannel) {
            setChannelApps([]);
            return;
        }

        setLoadingChannelApps(true);
        request({
            module: 'saChannels',
            method: 'channelsAppList',
            payload: { channelId },
            onSuccess: (response) => {
                const apps = response.data.data.data || [];
                // Filter by matching channel code AND name
                const matched = apps.filter(app =>
                    app.channel_code?.toLowerCase() === selectedChannel.code?.toLowerCase() &&
                    app.channel_name?.toLowerCase() === selectedChannel.name?.toLowerCase()
                );
                setChannelApps(matched);

                // If editing, pre-select the existing meta app ids (intersected with valid ones)
                if (preselectIds && Array.isArray(preselectIds)) {
                    const validIds = matched.map(a => a.meta_app_id.toString());
                    const keep = preselectIds
                        .map(id => id?.toString())
                        .filter(id => validIds.includes(id));
                    setFormData(prev => {
                        const updated = [...prev.channelEntries];
                        if (updated[0]) {
                            updated[0] = { ...updated[0], channelAppIds: keep };
                        }
                        return { ...prev, channelEntries: updated };
                    });
                }

                setLoadingChannelApps(false);
            },
            onError: (message) => {
                showError(message || 'Error fetching channel apps');
                setChannelApps([]);
                setLoadingChannelApps(false);
            },
            onFinally: () => setLoadingChannelApps(false)
        });
    };

    // Edit open — pre-fill with existing client, channel, and meta app ids
    const handleEditOpen = (row) => {
        setSelectedRow(row);
        const channelIdStr = row.channel_id?.toString() || '';
        const existingAppIds = Array.isArray(row.meta_app_ids)
            ? row.meta_app_ids.map(id => id?.toString())
            : [];

        setFormData({
            clientId: row.client_id?.toString() || '',
            channelEntries: [{
                channelId: channelIdStr,
                channelAppIds: existingAppIds
            }]
        });

        // Load channel apps for the pre-selected channel so the multi-select is populated
        if (channelIdStr && !isNoAppChannel(channelIdStr)) {
            fetchChannelAppsForChannel(channelIdStr, existingAppIds);
        } else {
            setChannelApps([]);
        }

        setEditOpen(true);
    };

    // Update
    const handleUpdate = () => {
        const entry = formData.channelEntries[0];
        if (!formData.clientId) {
            showWarning('Please select a client');
            return;
        }
        if (!entry.channelId) {
            showWarning('Please select a channel');
            return;
        }

        const noApp = isNoAppChannel(entry.channelId);
        if (!noApp && (!entry.channelAppIds || entry.channelAppIds.length === 0)) {
            showWarning('Please select at least one channel app');
            return;
        }

        const payload = {
            id: selectedRow.id,
            clientId: parseInt(formData.clientId),
            channelId: entry.channelId
        };

        if (!noApp) {
            payload.metaAppIds = entry.channelAppIds.map(id => parseInt(id));
        }

        setSaving(true);
        request({
            module: 'saChannels',
            method: 'clientChannelsUpdate',
            payload,
            onSuccess: (response) => {
                showSuccess(response.data.data.message || 'Client channel updated successfully');
                setEditOpen(false);
                resetForm();
                setSelectedRow(null);
                if (filterValues.clientId) {
                    fetchClientChannels(filterValues.clientId);
                } else {
                    fetchAllClientChannels();
                }
                setSaving(false);
            },
            onError: (message) => {
                showError(message || 'Error updating client channel');
                setSaving(false);
            },
            onFinally: () => setSaving(false)
        });
    };

    // Toggle - open confirmation
    const handleToggleOpen = (row) => {
        setSelectedRow(row);
        setToggleOpen(true);
    };

    // Toggle - confirm
    const handleToggleConfirm = () => {
        setTogglingId(selectedRow.id);
        setToggleOpen(false);
        request({
            module: 'saChannels',
            method: 'clientChannelsToggle',
            payload: { clientId: filterValues.clientId || selectedRow.client_id?.toString(), id: selectedRow.id },
            onSuccess: (response) => {
                showSuccess(response.data.data.message || 'Client channel toggled successfully');
                if (filterValues.clientId) {
                    fetchClientChannels(filterValues.clientId);
                } else {
                    fetchAllClientChannels();
                }
                setTogglingId(null);
                setSelectedRow(null);
            },
            onError: (message) => {
                showError(message || 'Error toggling client channel');
                setTogglingId(null);
                setSelectedRow(null);
            },
            onFinally: () => setTogglingId(null)
        });
    };

    // Load clients and channels on mount
    useEffect(() => {
        fetchClients();
        fetchChannels();
    }, []);

    // Fetch client channels when filter changes or on mount
    useEffect(() => {
        if (filterValues.clientId) {
            fetchClientChannels(filterValues.clientId);
        } else {
            fetchAllClientChannels();
        }
    }, [filterValues, refreshKey]);

    // Client options for dropdown
    const clientOptions = clients
        .map(c => ({
            value: c.id.toString(),
            label: c.comapny || `${c.first_name || ''} ${c.last_name || ''}`.trim() || `Client ${c.id}`
        }))
        .sort((a, b) => a.label?.localeCompare(b.label, undefined, { sensitivity: 'base' })) || [];

    // Channel options for dropdown
    const channelOptions = channels.map(ch => ({
        value: ch.id.toString(),
        label: ch.name
    }));

    // Form handlers
    const handleClientChange = (value) => {
        setFormData(prev => ({ ...prev, clientId: value }));
    };

    const handleChannelEntryChange = (index, field, value) => {
        setFormData(prev => {
            const updated = [...prev.channelEntries];
            updated[index] = { ...updated[index], [field]: value };
            // Reset selected channel apps whenever the channel changes
            if (field === 'channelId') {
                updated[index].channelAppIds = [];
            }
            return { ...prev, channelEntries: updated };
        });

        // Skip the API call entirely for sms/website/email — they don't use channel apps
        if (field === 'channelId') {
            if (isNoAppChannel(value)) {
                setChannelApps([]);
            } else {
                fetchChannelAppsForChannel(value);
            }
        }
    };

    const handleAddChannelEntry = () => {
        setFormData(prev => ({
            ...prev,
            // New entries start with an empty array of channel app ids (multi-select)
            channelEntries: [...prev.channelEntries, { channelId: '', channelAppIds: [] }]
        }));
    };

    const handleRemoveChannelEntry = (index) => {
        setFormData(prev => ({
            ...prev,
            channelEntries: prev.channelEntries.filter((_, i) => i !== index)
        }));
    };

    const resetForm = () => {
        setFormData(INITIAL_FORM);
        setChannelApps([]);
    };

    // Create
    const handleCreate = () => {
        if (!formData.clientId) {
            showWarning('Please select a client');
            return;
        }

        // Build the channels array. Each entry becomes either { channelId } for no-app
        // channels, or { channelId, metaAppIds: [...] } for channels with apps.
        const channelsPayload = [];
        for (const entry of formData.channelEntries) {
            if (!entry.channelId) {
                showWarning('Please select a channel for all entries');
                return;
            }

            const noApp = isNoAppChannel(entry.channelId);

            // Only validate channel apps for channels that actually use them
            if (!noApp && (!entry.channelAppIds || entry.channelAppIds.length === 0)) {
                showWarning('Please select at least one channel app for all entries');
                return;
            }

            const channelObj = { channelId: entry.channelId };

            if (!noApp) {
                // Convert dropdown string ids into integers for the API
                channelObj.metaAppIds = entry.channelAppIds.map(id => parseInt(id));
            }
            // For no-app channels (sms/website/email), metaAppIds is intentionally omitted

            channelsPayload.push(channelObj);
        }

        const payload = {
            clientId: parseInt(formData.clientId),
            channels: channelsPayload
        };

        setSaving(true);
        request({
            module: 'saChannels',
            method: 'clientChannelsBulkAdd',
            payload,
            onSuccess: (response) => {
                showSuccess(response.data.data.message || 'Client channels added successfully');
                setCreateOpen(false);
                resetForm();
                if (filterValues.clientId) {
                    fetchClientChannels(filterValues.clientId);
                } else {
                    fetchAllClientChannels();
                }
                setSaving(false);
            },
            onError: (message) => {
                showError(message || 'Error adding client channels');
                setSaving(false);
            },
            onFinally: () => setSaving(false)
        });
    };

    const isFormInvalid = () => {
        if (!formData.clientId) return true;
        if (formData.channelEntries.length === 0) return true;
        // Channel apps are only required for channels that actually use them
        for (const entry of formData.channelEntries) {
            if (!entry.channelId) return true;
            if (!isNoAppChannel(entry.channelId)) {
                if (!entry.channelAppIds || entry.channelAppIds.length === 0) return true;
            }
        }
        return saving;
    };

    // Filter handlers
    const handleFiltersApply = (filters) => {
        setFilterValues(filters);
        resetPage();
    };

    const handleFiltersClear = (filters) => {
        setFilterValues(filters);
        resetPage();
    };

    const handleRefresh = () => {
        setRefreshKey(prev => prev + 1);
    };

    // Filter fields config
    const filterFields = [
        {
            name: 'clientId',
            label: 'Client',
            type: 'searchable',
            options: clientOptions,
            defaultValue: '',
            placeholder: 'Search clients...'
        }
    ];

    // Table columns
    const headCells = [
        { id: 'id', numeric: false, disablePadding: false, label: 'ID' },
        // { id: 'client_id', numeric: false, disablePadding: false, label: 'Client Id' },
        // { id: 'channel_id', numeric: false, disablePadding: false, label: 'Channel Id' },
        {
            id: 'meta_app_ids',
            numeric: false,
            disablePadding: false,
            label: 'Meta App IDs',
            tooltip: true,
            customRow: row => Array.isArray(row.meta_app_ids) && row.meta_app_ids.length > 0
                ? row.meta_app_ids.join(', ')
                : '-'
        },
        {
            id: 'client_name',
            numeric: false,
            disablePadding: false,
            label: 'Client Name',
            msg: true,
            customRow: row => `${row.client_first_name || ''} ${row.client_last_name || ''}`.trim() || '-'
        },
        { id: 'channel_name', numeric: false, disablePadding: false, label: 'Channel Name', tooltip: true },
        { id: 'channel_code', numeric: false, disablePadding: false, label: 'Channel Code', tooltip: true },
        { id: 'channel_app', numeric: false, disablePadding: false, label: 'Channel App', tooltip: true },
        {
            id: 'is_enabled',
            numeric: false,
            disablePadding: false,
            label: 'Status',
            pill: true,
            pillConfig: {
                mapping: {
                    true: {
                        label: 'Active',
                        color: '#4caf50',
                        bgColor: 'rgba(76, 175, 80, 0.1)',
                        icon: <CheckCircleIcon fontSize="small" />
                    },
                    false: {
                        label: 'Inactive',
                        color: '#f44336',
                        bgColor: 'rgba(244, 67, 54, 0.1)',
                        icon: <CancelIcon fontSize="small" />
                    }
                },
                defaultColor: '#9e9e9e',
                defaultBgColor: 'rgba(158, 158, 158, 0.1)'
            }
        },
        { id: 'dt', numeric: false, disablePadding: false, label: 'Created', dt: true },
        { id: 'dtu', numeric: false, disablePadding: false, label: 'Updated', dt: true },
        { id: 'edit', numeric: false, disablePadding: false, label: 'Edit' },
        {
            id: 'toggle',
            numeric: false,
            disablePadding: false,
            label: 'Active',
            customRow: row => (
                <Switch
                    checked={row.is_enabled === true}
                    onChange={() => handleToggleOpen(row)}
                    disabled={togglingId === row.id}
                    className={classes.switch}
                    size="small"
                />
            )
        }
    ];

    // Shared form content — used by both create and edit modals.
    // `mode` controls small differences:
    //   - 'create' shows the "+ Add Channel" button and allows removing entries
    //   - 'edit'   shows a single, fixed channel entry (no add/remove)
    const renderFormContent = (mode = 'create') => {
        const selectedClientOption = clientOptions.find(opt => opt.value === formData.clientId) || null;
        const isEdit = mode === 'edit';

        return (
            <div className={classes.formContent}>
                <SearchableDropdown
                    label="Client *"
                    options={clientOptions}
                    value={selectedClientOption}
                    onChange={(newValue) => handleClientChange(newValue?.value || '')}
                    placeholder="Search clients..."
                    style={{ width: '100%' }}
                />

                <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginTop: 8 }}>
                    <Typography variant="subtitle1" style={{ fontWeight: 600 }} color="textPrimary">
                        {isEdit ? 'Channel' : 'Channels'}
                    </Typography>
                    {!isEdit && (
                        <ButtonOmniNew
                            onClick={handleAddChannelEntry}
                            variant="outlined"
                            label="+ Add Channel"
                            size="small"
                        />
                    )}
                </Box>

                {formData.channelEntries.map((entry, index) => {
                    const channelAppOptions = channelApps.map(app => ({
                        value: app.meta_app_id.toString(),
                        label: `${app.app_name} (${app.app_id})`
                    }));

                    // Hide the channel app dropdown entirely for sms/website/email
                    const noApp = isNoAppChannel(entry.channelId);

                    return (
                        <Box key={index} display="flex" flexDirection="column" style={{ gap: 8, border: `1px solid ${theme.palette.divider}`, padding: 12, borderRadius: 4 }}>
                            <Box display="flex" alignItems="flex-start" gap={1} style={{ gap: 8 }}>
                                <FormFieldOmni
                                    label="Channel *"
                                    variant="outlined"
                                    value={entry.channelId}
                                    onChange={(e) => handleChannelEntryChange(index, 'channelId', e.target.value)}
                                    type="select"
                                    options={channelOptions}
                                    size="small"
                                    fullWidth
                                    placeholder="Select channel"
                                />
                                {!isEdit && formData.channelEntries.length > 1 && (
                                    <DeleteIcon
                                        fontSize="large"
                                        style={{ cursor: 'pointer', color: theme.palette.secondary.value, flexShrink: 0, marginTop: 10 }}
                                        onClick={() => handleRemoveChannelEntry(index)}
                                    />
                                )}
                            </Box>

                            {/* Multi-select channel apps — only for channels that use apps.
                                Uses a native MUI Select so we can render a Checkbox inside
                                each MenuItem (FormFieldOmni doesn't support that). */}
                            {entry.channelId && !noApp && (
                                <FormControl
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    disabled={loadingChannelApps || channelAppOptions.length === 0}
                                >
                                    <InputLabel
                                        shrink
                                        style={{ color: theme.palette.text.primary, fontSize: 14, backgroundColor: theme.palette.background.paper, padding: '0 4px' }}
                                    >
                                        Channel Apps *
                                    </InputLabel>
                                    <Select
                                        multiple
                                        notched
                                        label="Channel Apps *"
                                        value={entry.channelAppIds || []}
                                        onChange={(e) => handleChannelEntryChange(index, 'channelAppIds', e.target.value)}
                                        renderValue={(selected) => {
                                            if (!selected || selected.length === 0) {
                                                return loadingChannelApps
                                                    ? 'Loading...'
                                                    : (channelAppOptions.length === 0 ? 'No matching channel apps' : 'Select channel apps');
                                            }
                                            return selected
                                                .map(val => channelAppOptions.find(o => o.value === val)?.label || val)
                                                .join(', ');
                                        }}
                                        displayEmpty
                                        style={{ color: theme.palette.text.primary, fontSize: 14 }}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    backgroundColor: theme.palette.type === 'dark' ? '#1D1F1F !important' : '#ffffff !important',
                                                    color: theme.palette.text.primary,
                                                }
                                            }
                                        }}
                                    >
                                        {channelAppOptions.map(option => {
                                            const checked = (entry.channelAppIds || []).indexOf(option.value) > -1;
                                            return (
                                                <MenuItem
                                                    key={option.value}
                                                    value={option.value}
                                                    style={{
                                                        color: theme.palette.text.primary,
                                                        backgroundColor: theme.palette.type === 'dark' ? '#1D1F1F' : '#ffffff',
                                                    }}
                                                >
                                                    <Checkbox
                                                        checked={checked}
                                                        size="small"
                                                        style={{ padding: 4, marginRight: 8, color: theme.palette.text.primary }}
                                                    />
                                                    <ListItemText
                                                        primary={option.label}
                                                        primaryTypographyProps={{ style: { fontSize: 14, color: theme.palette.text.primary } }}
                                                    />
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </FormControl>
                            )}
                        </Box>
                    );
                })}
            </div>
        );
    };

    // Create dialog actions
    const createActions = (
        <div className={classes.buttonContainer}>
            <ButtonOmniNew onClick={() => { setCreateOpen(false); resetForm(); }} variant="outlined" label="Cancel" disabled={saving} />
            <ButtonOmniNew onClick={handleCreate} variant="outlined" label={saving ? 'Adding...' : 'Add'} disabled={isFormInvalid()} />
        </div>
    );

    return (
        <div className="w-full mx-auto">
            {/* Filters & Create */}
            <DateFilterWithExport
                showDateRange={false}
                showExport={false}
                filterFields={filterFields}
                filterValues={filterValues}
                onFiltersApply={handleFiltersApply}
                onFiltersClear={handleFiltersClear}
                showRefresh={true}
                onRefresh={handleRefresh}
                customRightContent={
                    <ButtonOmniNew
                        buttonType="create"
                        label="Add Client Channel"
                        onClick={() => { resetForm(); setCreateOpen(true); }}
                        variant="outlined"
                    />
                }
            />

            {/* Table */}
            <TableOmniNewAnimated
                rows={data}
                headCells={headCells}
                perPageRows={currentParams.limit}
                setCurrentParams={setCurrentParams}
                curPage={currentParams.page}
                rowsPerPageOptions={[10, 25, 50, 100]}
                loading={isLoading}
                onEditClick={handleEditOpen}
            />

            {/* Create Dialog */}
            <ModalOmni
                open={createOpen}
                handleClose={() => { setCreateOpen(false); resetForm(); }}
                title="Add Client Channel"
                content={renderFormContent('create')}
                actions={createActions}
                customWidth={600}
                disableBackdropClick
            />

            {/* Edit Dialog — same layout as create, single channel entry */}
            <ModalOmni
                open={editOpen}
                handleClose={() => { setEditOpen(false); resetForm(); setSelectedRow(null); }}
                title="Edit Client Channel"
                content={renderFormContent('edit')}
                actions={
                    <div className={classes.buttonContainer}>
                        <ButtonOmniNew onClick={() => { setEditOpen(false); resetForm(); setSelectedRow(null); }} variant="outlined" label="Cancel" disabled={saving} />
                        <ButtonOmniNew onClick={handleUpdate} variant="outlined" label={saving ? 'Updating...' : 'Update'} disabled={isFormInvalid()} />
                    </div>
                }
                customWidth={600}
                disableBackdropClick
            />

            {/* Toggle Confirmation Dialog */}
            <ModalOmni
                disableBackdropClick
                open={toggleOpen}
                handleClose={() => { setToggleOpen(false); setSelectedRow(null); }}
                title={selectedRow?.is_enabled ? 'Deactivate Client Channel' : 'Activate Client Channel'}
                content={
                    <div className={classes.toggleContent}>
                        <Typography variant="body1" color="textPrimary">
                            Are you sure you want to {selectedRow?.is_enabled ? 'deactivate' : 'activate'} this client channel
                            {selectedRow?.channel_name ? ` "${selectedRow.channel_name}"` : ''}?
                        </Typography>
                    </div>
                }
                actions={
                    <div className={classes.buttonContainer}>
                        <ButtonOmniNew onClick={() => { setToggleOpen(false); setSelectedRow(null); }} variant="outlined" label="Cancel" />
                        <ButtonOmniNew onClick={handleToggleConfirm} variant="outlined" label={selectedRow?.is_enabled ? 'Deactivate' : 'Activate'} />
                    </div>
                }
                customWidth={450}
            />

            <SnackbarOmni {...snackbar} />
        </div>
    );
}

export default React.memo(ClientChannelsTab);